<template>
  <div class="mb-5">
    <label v-if="label" class="label-input">{{ label }}</label>
    <div class="group-item">
      <v-row>
        <v-col v-if="showBackgroundImage" cols="12">
          <label>Background Image</label>
          <UploadImage
            :image="{ imageSrc: safeSettings.backgroundImage }"
            :height="`140`"
            :typeUpload="'image'"
            @callback="obj => updateSettings('backgroundImage', (obj && obj.imageSrc) || '')"
            @callbackDelete="() => updateSettings('backgroundImage', '')"
          />
        </v-col>
        <v-col v-if="showBackgroundColor" cols="12" md="6">
          <label>Background Color</label>
          <div class="d-flex align-center gap-3">
            <input
              type="color"
              style="width: 40px; height: 40px; margin-right: 12px"
              :value="safeSettings.backgroundColor"
              @input="e => updateSettings('backgroundColor', e.target.value)"
            />
            <v-text-field
              :value="safeSettings.backgroundColor"
              hide-details
              class="mt-0"
              @input="value => updateSettings('backgroundColor', value)"
            />
          </div>
        </v-col>
        <v-col v-if="showTextColor" cols="12" md="6">
          <label>Text Color</label>
          <div class="d-flex align-center gap-3">
            <input
              type="color"
              style="width: 40px; height: 40px; margin-right: 12px"
              :value="safeSettings.textColor"
              @input="e => updateSettings('textColor', e.target.value)"
            />
            <v-text-field
              :value="safeSettings.textColor"
              hide-details
              class="mt-0"
              @input="value => updateSettings('textColor', value)"
            />
          </div>
        </v-col>
        <v-col v-if="showPadding" cols="12" md="6">
          <v-checkbox
            :input-value="safeSettings.paddingTop"
            label="Top Padding"
            hide-details
            class="mt-0"
            @change="value => updateSettings('paddingTop', !!value)"
          />
        </v-col>
        <v-col v-if="showPadding" cols="12" md="6">
          <v-checkbox
            :input-value="safeSettings.paddingBottom"
            label="Bottom Padding"
            hide-details
            class="mt-0"
            @change="value => updateSettings('paddingBottom', !!value)"
          />
        </v-col>
        <v-col v-if="showOverlay" cols="12">
          <v-checkbox
            :input-value="safeSettings.showOverlay"
            label="Show Overlay"
            hide-details
            class="mt-0 mb-2"
            @change="value => updateSettings('showOverlay', !!value)"
          />
          <v-row v-if="safeSettings.showOverlay">
            <v-col cols="12" md="6">
              <label>Overlay Color</label>
              <div class="d-flex align-center gap-3">
                <input
                  type="color"
                  style="width: 40px; height: 40px; margin-right: 12px"
                  :value="safeSettings.overlayColor"
                  @input="e => updateSettings('overlayColor', e.target.value)"
                />
                <v-text-field
                  :value="safeSettings.overlayColor"
                  hide-details
                  class="mt-0"
                  @input="value => updateSettings('overlayColor', value)"
                />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <label>Overlay Opacity (0 - 1)</label>
              <v-text-field
                :value="safeSettings.overlayOpacity"
                type="number"
                step="0.1"
                hide-details
                class="mt-0"
                @input="value => updateSettings('overlayOpacity', toNumber(value))"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col v-if="showContainerStyle && containerPage" cols="12">
          <v-checkbox
            :input-value="safeContainerPage.enableStyle"
            label="Enable Style Container"
            hide-details
            class="mt-0 mb-2"
            @change="value => updateContainerPage('enableStyle', !!value)"
          />
          <div v-if="safeContainerPage.enableStyle">
            <label>Container Background</label>
            <div class="d-flex align-center gap-3">
              <input
                type="color"
                style="width: 40px; height: 40px; margin-right: 12px"
                :value="safeContainerPage.background"
                @input="e => updateContainerPage('background', e.target.value)"
              />
              <v-text-field
                :value="safeContainerPage.background"
                hide-details
                class="mt-0"
                @input="value => updateContainerPage('background', value)"
              />
            </div>
          </div>
        </v-col>
        <v-col v-if="showCustomWidth" cols="12">
          <v-checkbox
            :input-value="safeSettings.useCustomWidth"
            label="Use Custom Width"
            hide-details
            class="mt-0 mb-2"
            @change="value => updateSettings('useCustomWidth', !!value)"
          />
          <div v-if="safeSettings.useCustomWidth">
            <label>Custom Width</label>
            <v-text-field
              :value="safeSettings.customWidth"
              type="number"
              suffix="px"
              hide-details
              class="mt-0"
              @input="value => updateSettings('customWidth', toNumber(value))"
            />
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import UploadImage from '@/views/website/themes/homepage/common/form/upload-file/upload-image.vue';

const defaultSettings = {
  paddingTop: true,
  paddingBottom: true,
  backgroundColor: '#FFFFFF',
  textColor: '#000000',
  overlayColor: '#000000',
  overlayOpacity: 0,
  showOverlay: false,
  backgroundImage: '',
  useCustomWidth: false,
  customWidth: 1204,
};

const defaultContainerPage = {
  enableStyle: false,
  background: '#FFFFFF',
};

export default {
  name: 'SectionLayout',
  components: { UploadImage },
  props: {
    settings: {
      type: Object,
      required: true,
      default: () => ({ ...defaultSettings }),
    },
    containerPage: {
      type: Object,
      default: null,
    },
    label: {
      type: String,
      default: 'Layout',
    },
    showBackgroundImage: {
      type: Boolean,
      default: true,
    },
    showBackgroundColor: {
      type: Boolean,
      default: true,
    },
    showTextColor: {
      type: Boolean,
      default: true,
    },
    showPadding: {
      type: Boolean,
      default: true,
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
    showContainerStyle: {
      type: Boolean,
      default: true,
    },
    showCustomWidth: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    safeSettings() {
      return { ...defaultSettings, ...(this.settings || {}) };
    },
    safeContainerPage() {
      return { ...defaultContainerPage, ...(this.containerPage || {}) };
    },
  },
  methods: {
    updateSettings(key, value) {
      this.$set(this.settings, key, value);
      this.$emit('update:settings', this.settings);
      this.$emit('change', this.settings);
    },
    updateContainerPage(key, value) {
      if (!this.containerPage) return;
      this.$set(this.containerPage, key, value);
      this.$emit('update:container-page', this.containerPage);
      this.$emit('change-container-page', this.containerPage);
    },
    toNumber(value) {
      const num = Number(value);
      return Number.isNaN(num) ? 0 : num;
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
</style>
